import React, { useState, useEffect, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import AuthContext from './AuthContext';
import { lookupMembership, claimMembership, getOrg } from '../firebase/orgs';
import { loadEffectiveCapabilities, ALL_ALLOWED } from '../firebase/capabilities';
import { SUPERADMIN_ORG_ID } from '../config/superadminOrg';

// effectiveOrg for superadmin sessions: the SUPERADMIN_ORG_ID org doc, read once
// per sign-in (rules let superadmins read any org doc via isMemberOf). A failed
// read returns null, which keeps every consent-gated path (bulk capture, the
// Part 3 plan-review log) off; it never un-provisions the session.
async function loadSuperadminHomeOrg() {
  try {
    return (await getOrg(SUPERADMIN_ORG_ID)) || null;
  } catch (e) {
    console.warn('[auth] could not load the superadmin home org; consent-gated capture stays off:', e?.message || e);
    return null;
  }
}

export default function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [orgMember, setOrgMember]   = useState(null);
  const [org, setOrg]               = useState(null);
  // The org whose consent flags (dataPooling) gate this session's writes: the same
  // object as `org` for members; for superadmin sessions (org null) the
  // SUPERADMIN_ORG_ID doc. Null when unknown, which keeps consent-gated paths off.
  const [effectiveOrg, setEffectiveOrg] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [provisioned, setProvisioned] = useState(true); // false = signed in but no org assigned
  // Org-owned capabilities (2026-08-22): the member's effective permission
  // set + the org model policy, loaded alongside the org doc so every gated
  // surface (useCapability) sees the right set on first render. Always
  // present; ALL_ALLOWED for superadmins, unprovisioned, and signed-out.
  const [capabilities, setCapabilities] = useState(ALL_ALLOWED);

  useEffect(() => {
    if (!auth) {
      setAuthLoading(false);
      return;
    }
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      // Fail safe across sign-in, sign-out and account switches: no consent until
      // the branch below has loaded the org this session writes under.
      setEffectiveOrg(null);
      if (u?.uid) {
        try {
          // Check superadmin via Firebase Auth custom claim — no Firestore read needed.
          // Set by seed script: admin.auth().setCustomUserClaims(uid, { superadmin: true })
          const tokenResult = await u.getIdTokenResult(true); // force-refresh token
          const superAdmin  = tokenResult.claims.superadmin === true;
          setIsSuperAdmin(superAdmin);

          if (superAdmin) {
            // Superadmins bypass org membership lookup entirely; `org` stays null so
            // Header, PilotExpirationBanner, LandingPage and useOrgRole are unaffected.
            // Their writes land in SUPERADMIN_ORG_ID (AppProvider), so that org's doc
            // is their effectiveOrg (spec 2026-10-07 Part 0.5).
            const homeOrg = await loadSuperadminHomeOrg();
            setOrgMember(null);
            setOrg(null);
            setEffectiveOrg(homeOrg);
            setProvisioned(true);
            setCapabilities({ ...ALL_ALLOWED, denied: new Set() });
          } else if (u.email) {
            const membership = await lookupMembership(u.email);
            if (membership) {
              // Claim first — writes /userOrgs/{uid}, which Firestore rules require
              // for reading /orgs/{orgId}. Without this, getOrg throws permission denied
              // on first sign-in and the user sees the "not provisioned" screen.
              await claimMembership(membership.orgId, u.email, u.uid);
              const orgDoc = await getOrg(membership.orgId);
              setOrgMember({ orgId: membership.orgId, roles: membership.roles });
              setOrg(orgDoc);
              setEffectiveOrg(orgDoc);
              setProvisioned(true);
              setCapabilities(await loadEffectiveCapabilities({ orgId: membership.orgId, email: u.email, org: orgDoc }));
            } else {
              setOrgMember(null);
              setOrg(null);
              setProvisioned(false);
              setCapabilities({ ...ALL_ALLOWED, denied: new Set() });
            }
          }
        } catch (e) {
          // Auth lookup failed — user will see "not provisioned" screen
          setIsSuperAdmin(false);
          setOrgMember(null);
          setOrg(null);
          setProvisioned(false);
          setCapabilities({ ...ALL_ALLOWED, denied: new Set() });
        }
      } else {
        setIsSuperAdmin(false);
        setOrgMember(null);
        setOrg(null);
        setProvisioned(true);
        setCapabilities({ ...ALL_ALLOWED, denied: new Set() });
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // Re-read org defaults, model policy, and the member's own overrides.
  // Called by the Admin Portal editors after a save so an admin editing
  // their own org sees the change without a reload.
  const refreshCapabilities = useCallback(async () => {
    if (isSuperAdmin || !user?.email || !orgMember?.orgId) {
      const next = { ...ALL_ALLOWED, denied: new Set() };
      setCapabilities(next);
      return next;
    }
    const next = await loadEffectiveCapabilities({ orgId: orgMember.orgId, email: user.email });
    setCapabilities(next);
    return next;
  }, [isSuperAdmin, user, orgMember]);

  const signInWithGoogle = async () => {
    if (!auth) throw new Error('Firebase not configured');
    return signInWithPopup(auth, googleProvider);
  };

  const signInWithEmail = async (email, password) => {
    if (!auth) throw new Error('Firebase not configured');
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signUpWithEmail = async (email, password) => {
    if (!auth) throw new Error('Firebase not configured');
    return createUserWithEmailAndPassword(auth, email, password);
  };

  const resetPassword = async (email) => {
    if (!auth) throw new Error('Firebase not configured');
    return sendPasswordResetEmail(auth, email);
  };

  const signOut = async () => {
    if (!auth) return;
    return firebaseSignOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, authLoading, orgMember, org, effectiveOrg, isSuperAdmin, provisioned, capabilities, refreshCapabilities, signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
